import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createEventParams, createEvent } from "supabase/events";
import { Database } from "types/supabase";
import { queryClient } from "utils";

type CreatedEvent = Database["public"]["Tables"]["events"]["Row"];

/**
 * FBIMP-13 / spec 10.3: "Keep existing navigation for normal event creation,
 * but suppress per-event navigation during application conversion."
 *
 * `navigate(-1)` lives INSIDE the hook rather than in a caller-supplied
 * callback so that suppressing it is a contract
 * (`useCreateEvent({ navigateOnSuccess: false })`) rather than a convention
 * (passing a no-op function, which the next edit quietly re-fills). A weekly
 * application creating four occurrences used to fire `navigate(-1)` four
 * times; the conversion path now navigates once, at the end, itself.
 */
export type UseCreateEventOptions = {
  navigateOnSuccess?: boolean;
  onSuccess?: (created: CreatedEvent) => void;
};

export const useCreateEvent = (options: UseCreateEventOptions = {}) => {
  // Defaults to today's behaviour: a standalone caller still navigates back.
  const { navigateOnSuccess = true, onSuccess } = options;
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (event: createEventParams) => {
      return createEvent(event);
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({
        queryKey: ["events"],
      });

      if (navigateOnSuccess) {
        navigate(-1);
      }

      if (onSuccess) {
        onSuccess(created);
      }
    },
  });

  return mutation;
};
